import React, { useState } from "react";
import EbDialog from "../../../ExecutionBlueprint/ExecutionBlueprintLiveReview/_shared/EbDialog";
import { LIMITS, OBJECTIVE_TYPE } from "../../_config/quarterlyOkrConfig";
import { ContributorChips } from "../../_shared/QokrPeoplePicker/QokrPeoplePicker";
import QokrSelect from "../../_shared/QokrSelect/QokrSelect";
import QokrRowsField, {
  rowsFrom,
  toRows,
} from "../../_shared/QokrRowsField/QokrRowsField";
import "../../_shared/_qokr-forms.scss";
import "./_live-objective-editor.scss";

// ═══════════════════════════════════════════════════════════════════════════
// Add / edit one objective in the live review (§10 `qokr_lr_save_objective`,
// full-card save). Company: owner, contributors and (optionally) the Annual
// OKR it serves. Individual: the person (owner) and the company objective
// it serves. The person can be changed on edit too (2026-11-03 — it used to
// be create-only). The link can be set or changed on create AND on edit
// (2026-10-23 — it used to be create-only). Kept ids keep their rows; the
// `version` guards against two editors.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * @param type           OBJECTIVE_TYPE.
 * @param objective      The row being edited, or null to create.
 * @param roster         `[{ user_id, name, role }]` from the snapshot.
 * @param annualOptions  `[{ value, label }]` Annual OKRs (company create).
 * @param companyOptions `[{ value, label }]` company objectives (individual).
 * @param defaults       `{ userId, companyObjectiveId }` for a new individual.
 */
const LiveObjectiveEditor = ({
  type,
  objective,
  roster,
  annualOptions = [],
  companyOptions = [],
  defaults = {},
  saving,
  onSave,
  onClose,
}) => {
  const isCompany = type === OBJECTIVE_TYPE.COMPANY;
  const creating = !objective;
  const [text, setText] = useState(objective?.objective || "");
  const [keyResults, setKeyResults] = useState(
    rowsFrom(objective?.key_results, "key_result_id"),
  );
  const [initiatives, setInitiatives] = useState(
    rowsFrom(objective?.initiatives, "initiative_id"),
  );
  const [ownerId, setOwnerId] = useState(
    objective?.owner?.user_id ? String(objective.owner.user_id) : null,
  );
  const [contributorIds, setContributorIds] = useState(
    (objective?.contributors || []).map((p) => String(p.user_id)),
  );
  // Ids as strings: StandardSelect matches its value with `===`, and the
  // snapshot's ids can arrive as numbers or strings.
  const idOf = (value) =>
    value == null || value === "" ? null : String(value);
  const asOptions = (list) =>
    list.map((option) => ({ ...option, value: String(option.value) }));
  const annualSelect = asOptions(annualOptions);
  const peopleSelect = roster.map((member) => ({
    value: String(member.user_id),
    label: member.name,
    person: member,
  }));
  const companySelect = asOptions(companyOptions);
  const [annualId, setAnnualId] = useState(
    idOf(objective?.annual_objective_id),
  );
  const [memberId, setMemberId] = useState(
    objective?.member?.user_id || defaults.userId || null,
  );
  const [companyObjectiveId, setCompanyObjectiveId] = useState(
    idOf(objective?.company_objective_id || defaults.companyObjectiveId),
  );

  const filledKrs = keyResults.filter((row) => row.statement.trim());
  const filledInitiatives = initiatives.filter((row) => row.statement.trim());
  const invalid = !text.trim()
    ? "An objective needs a statement"
    : filledKrs.length < LIMITS.KEY_RESULTS_MIN
      ? "Add at least one key result"
      : filledKrs.length > LIMITS.KEY_RESULTS_MAX
        ? `Keep it to ${LIMITS.KEY_RESULTS_MAX} key results or fewer`
        : filledInitiatives.length < LIMITS.INITIATIVES_MIN
          ? "Add at least one initiative"
          : filledInitiatives.length > LIMITS.INITIATIVES_MAX
            ? `Keep it to ${LIMITS.INITIATIVES_MAX} initiatives or fewer`
            : isCompany && !ownerId
              ? "Choose an owner"
              : !isCompany && !memberId
                ? "Choose an owner"
                : null;

  const submit = () => {
    const card = {
      objective: text.trim(),
      key_results: toRows(keyResults, "key_result_id"),
      initiatives: toRows(initiatives, "initiative_id"),
      ...(isCompany && ownerId ? { owner_user_id: ownerId } : {}),
      contributor_ids: contributorIds,
    };
    onSave({
      type,
      ...(objective
        ? { objective_id: objective.objective_id, version: objective.version }
        : {}),
      // Sent when set, and as null when an existing link was cleared.
      ...(isCompany && (annualId || objective?.annual_objective_id)
        ? { annual_objective_id: annualId || null }
        : {}),
      ...(!isCompany
        ? { user_id: memberId, company_objective_id: companyObjectiveId }
        : {}),
      card,
    });
  };

  // ⚠️ LAYOUT OVERRIDES VIA `qokr-lr-dialog` (2026-10-21). EbDialog scrolls
  // as ONE box, so in a long form the title and the Save button both scrolled
  // out of view, and its stacked 44px icon chip over the title read as a
  // confirm dialog, not a form. Here: a compact inline header and the actions
  // stay pinned, only the body scrolls, and people sit side by side. Scoped
  // by class so the EXW dialogs that share EbDialog are untouched.
  return (
    <EbDialog
      isOpen
      size="lg"
      tone="primary"
      className="qokr-lr-dialog"
      icon={
        <i
          className={`fa-solid ${creating ? "fa-plus" : "fa-pen"}`}
          aria-hidden="true"
        />
      }
      title={
        creating
          ? `Add ${isCompany ? "a company" : "an individual"} objective`
          : "Edit objective"
      }
      subtitle="Saved for everyone in the live review."
      busy={saving}
      onClose={onClose}
      actions={[
        { label: "Cancel", variant: "ghost", onClick: onClose },
        {
          label: creating ? "Add objective" : "Save changes",
          loadingLabel: "Saving…",
          variant: "primary",
          loading: saving,
          disabled: Boolean(invalid),
          onClick: submit,
        },
      ]}
    >
      <div className="qokr-lr-editor">
        <div className="qokr-lr-editor__field">
          <span className="qokr-lr-editor__label">Objective</span>
          <textarea
            className="qokr-textarea"
            rows={2}
            value={text}
            maxLength={LIMITS.OBJECTIVE_MAX_CHARS}
            onChange={(event) => setText(event.target.value)}
            aria-label="Objective"
          />
        </div>

        {/* The link straight under the objective, on add and on edit: it
            is what the objective serves, so it is read with the statement.
            (Lower down, its menu ran off the bottom of the dialog.) */}
        {isCompany && annualSelect.length ? (
          <div className="qokr-lr-editor__field">
            <span className="qokr-lr-editor__label">
              Linked Annual OKR (optional)
            </span>
            <QokrSelect
              options={annualSelect}
              value={annualId}
              onChange={setAnnualId}
              clearable
              icon="fa-solid fa-link"
              placeholder="Not linked"
              ariaLabel="Linked Annual OKR"
            />
          </div>
        ) : null}
        {!isCompany && companySelect.length ? (
          <div className="qokr-lr-editor__field">
            <span className="qokr-lr-editor__label">
              Linked company objective
            </span>
            <QokrSelect
              options={companySelect}
              value={companyObjectiveId}
              onChange={setCompanyObjectiveId}
              clearable
              icon="fa-solid fa-link"
              placeholder="Not linked"
              ariaLabel="Linked company objective"
            />
          </div>
        ) : null}

        <QokrRowsField
          label="Key results"
          rows={keyResults}
          min={LIMITS.KEY_RESULTS_MIN}
          max={LIMITS.KEY_RESULTS_MAX}
          maxChars={LIMITS.KEY_RESULT_MAX_CHARS}
          placeholder="What will be true at the end of the quarter?"
          onChange={setKeyResults}
        />
        <QokrRowsField
          label="Initiatives"
          rows={initiatives}
          min={LIMITS.INITIATIVES_MIN}
          max={LIMITS.INITIATIVES_MAX}
          maxChars={LIMITS.INITIATIVE_MAX_CHARS}
          placeholder="What will you do to move the key results?"
          onChange={setInitiatives}
        />

        {isCompany ? (
          <>
            {/* Owner, then contributors under it (2026-10-23 — they used to
                sit side by side). */}
            <div className="qokr-lr-editor__field">
              <span className="qokr-lr-editor__label">Owner</span>
              <QokrSelect
                options={peopleSelect}
                value={ownerId}
                onChange={(id) => {
                  setOwnerId(id != null ? String(id) : null);
                  setContributorIds((ids) =>
                    ids.filter((x) => x !== String(id)),
                  );
                }}
                placeholder="Choose an owner"
                ariaLabel="Owner"
              />
            </div>
            <div className="qokr-lr-editor__field">
              <span className="qokr-lr-editor__label">Contributors</span>
              <ContributorChips
                members={roster}
                value={contributorIds}
                excludeIds={[ownerId].filter(Boolean)}
                onChange={setContributorIds}
              />
            </div>
          </>
        ) : null}

        {!isCompany ? (
          <>
            {/* Owner, then contributors under it — same place and label as
                the company editor (2026-11-03 — it used to sit at the top,
                labelled "Person"). */}
            <div className="qokr-lr-editor__field">
              <span className="qokr-lr-editor__label">Owner</span>
              <QokrSelect
                options={peopleSelect}
                value={memberId != null ? String(memberId) : null}
                onChange={(id) => {
                  setMemberId(id != null ? String(id) : null);
                  // The owner is never also a contributor.
                  setContributorIds((ids) =>
                    ids.filter((x) => x !== String(id)),
                  );
                }}
                placeholder="Choose an owner"
                ariaLabel="Owner"
              />
            </div>
            <div className="qokr-lr-editor__field">
              <span className="qokr-lr-editor__label">Contributors</span>
              <ContributorChips
                members={roster}
                value={contributorIds}
                excludeIds={[memberId].filter(Boolean).map(String)}
                onChange={setContributorIds}
              />
            </div>
          </>
        ) : null}

        {invalid ? <p className="qokr-field-hint mb-0">{invalid}</p> : null}
      </div>
    </EbDialog>
  );
};

export default LiveObjectiveEditor;
